/**
 * Экономика года: уровень работы, ожидаемый доход, расходы, долг и банкротство.
 *
 * Модель (план, Task 9): доход = ВВП на душу (ППС, год жизни) × множитель
 * образования × множитель уровня работы × логнормальный шум, чьё среднее
 * сдвигает дециль семьи, а разброс — неравенство (Джини). Расходы —
 * доля дохода плюс по 7% на ребёнка; нехватка уходит в долг под 8% в год;
 * долг больше трёх годовых доходов списывается банкротством.
 * Без работы ни дохода, ни расходов: семья содержит.
 *
 * Деньги и долг — доллары ППС, всегда конечные и ≥ 0. Все случайности —
 * из переданного `Rng`; функции не мутируют вход.
 */
import { getValue, type Dataset } from '@life/data';
import { clampStats } from './focus';
import type { Rng } from './rng';
import type { Education, Job, Life, LogEntry } from './types';

export type JobLevel = Job['level'];

/** Базовый уровень работы по образованию. */
const BASE_LEVEL: Readonly<Record<Education, JobLevel>> = {
  none: 0,
  primary: 0,
  lowerSec: 1,
  upperSec: 1,
  vocational: 2,
  university: 2,
};

/** Множитель дохода по образованию. */
export const EDUCATION_MULT: Readonly<Record<Education, number>> = {
  none: 0.35,
  primary: 0.5,
  lowerSec: 0.7,
  upperSec: 0.9,
  vocational: 1.0,
  university: 1.6,
};

/** Множитель дохода по уровню работы. */
export const LEVEL_MULT: Readonly<Record<JobLevel, number>> = { 0: 0.6, 1: 1.0, 2: 1.5, 3: 2.5 };

export const MAX_JOB_LEVEL: JobLevel = 3;
/** Делитель шанса поднять уровень при найме: p = knowledge / 150. */
const LEVEL_BUMP_DIVISOR = 150;
/** Сдвиг среднего шума за дециль относительно середины (5.5). */
const DECILE_SHIFT = 0.08;
/** Базовый разброс шума дохода; к нему прибавляется Джини (0–1) / 2. */
const BASE_SIGMA = 0.25;
/**
 * Доля дохода, уходящая на жизнь, и доля на каждого ребёнка.
 * 0.07 на ребёнка — калибровка этапа 1: при 0.1 многодетные семьи бедных
 * стран (5 детей → 1.2·доход) уходили в хронический долг и банкротились в
 * 19 % жизней; при 0.07 банкротств ≈ 3 % (цель — единицы процентов).
 */
export const EXPENSE_SHARE = 0.7;
export const CHILD_EXPENSE_SHARE = 0.07;
/** Годовая ставка по долгу. */
export const DEBT_RATE = 0.08;
/** Долг выше стольких годовых доходов — банкротство. */
export const BANKRUPTCY_DEBT_TO_INCOME = 3;
const BANKRUPTCY_HAPPINESS = -15;
const CAREER_RAISE = 1.04;
const CAREER_PROMOTION_CHANCE = 0.15;
const WORK_RAISE = 1.02;

/** Ключ названия работы по уровню (`job.level<n>`). */
export function jobTitleKey(level: JobLevel): string {
  return `job.level${level}`;
}

/** Уровень работы при найме: база по образованию, +1 с p = knowledge/150, не выше 3. */
export function jobLevelFor(education: Education, knowledge: number, rng: Rng): JobLevel {
  const base = BASE_LEVEL[education];
  const p = Math.max(0, Math.min(1, knowledge / LEVEL_BUMP_DIVISOR));
  if (base < MAX_JOB_LEVEL && rng.chance(p)) return (base + 1) as JobLevel;
  return base;
}

/** Неотрицательная конечная сумма; NaN, Infinity и отрицательные → 0. */
function clampMoney(x: number): number {
  return Number.isFinite(x) && x > 0 ? x : 0;
}

/**
 * Ожидаемый годовой доход (PPP$) этого человека в его стране и году.
 * Уровень берётся из `life.job`, а без работы разыгрывается `jobLevelFor`
 * (так решение «первая работа» может предложить доход до найма).
 */
export function expectedIncome(ds: Dataset, life: Life, rng: Rng): number {
  const year = life.birthYear + life.age;
  const base = getValue(ds, life.iso3, 'gdpPpp', year).v;
  const gini = getValue(ds, life.iso3, 'gini', year).v;
  const level = life.job ? life.job.level : jobLevelFor(life.education, life.stats.knowledge, rng);
  const mu = (life.familyDecile - 5.5) * DECILE_SHIFT;
  const sigma = BASE_SIGMA + clampMoney(gini) / 2;
  const noise = rng.logNormal(mu, sigma);
  return clampMoney(base * EDUCATION_MULT[life.education] * LEVEL_MULT[level] * noise);
}

/**
 * Бонус к шансу повышения в этом году (мини-игра «Смена», G11): ставится
 * исходом игры, читается здесь и снимается экономикой в том же тике.
 */
export const FLAG_PROMOTION_BONUS = 'promotionBonus';

function promotionBonus(life: Life): number {
  const raw = life.flags[FLAG_PROMOTION_BONUS];
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : 0;
}

function promoted(job: Job, income: number): Job {
  const nextLevel = (job.level + 1) as JobLevel;
  return { titleKey: jobTitleKey(nextLevel), level: nextLevel, income: clampMoney((income * LEVEL_MULT[nextLevel]) / LEVEL_MULT[job.level]) };
}

/** Годовые изменения работы по фокусу: прибавка и шанс повышения (плюс бонус «Смены», если он есть). */
function advanceJob(job: Job, life: Life, rng: Rng): Job {
  const bonus = promotionBonus(life);
  if (life.focus === 'career') {
    const income = job.income * CAREER_RAISE;
    if (job.level < MAX_JOB_LEVEL && rng.chance(CAREER_PROMOTION_CHANCE + bonus)) return promoted(job, income);
    return { ...job, income: clampMoney(income) };
  }
  const income = life.focus === 'work' ? job.income * WORK_RAISE : job.income;
  if (bonus > 0 && job.level < MAX_JOB_LEVEL && rng.chance(bonus)) return promoted(job, income);
  return { ...job, income: clampMoney(income) };
}

function withoutPromotionBonus(flags: Life['flags']): Life['flags'] {
  if (!(FLAG_PROMOTION_BONUS in flags)) return flags;
  const out: Life['flags'] = {};
  for (const [k, v] of Object.entries(flags)) if (k !== FLAG_PROMOTION_BONUS) out[k] = v;
  return out;
}

/**
 * Экономика одного года. Порядок: переводы → проценты на перенесённый долг →
 * работа по фокусу → доход и расходы → нехватка в долг → проверка банкротства.
 * Возвращает новую жизнь и записи журнала (пока только `economy.bankruptcy`).
 */
export function applyEconomy(
  ds: Dataset,
  life: Life,
  rng: Rng,
  transfersIn: number,
): { life: Life; entries: LogEntry[] } {
  const entries: LogEntry[] = [];
  let money = clampMoney(life.money) + clampMoney(transfersIn);
  // Проценты только на долг прошлых лет; нехватка этого года пока без процентов.
  let debt = clampMoney(life.debt) * (1 + DEBT_RATE);
  let job: Job | null = life.job;
  let stats = life.stats;
  // Бонус повышения «Смены» действует один год: снимается здесь независимо от работы.
  let flags = withoutPromotionBonus(life.flags);

  if (job) {
    job = advanceJob(job, life, rng);
    const income = clampMoney(job.income);
    const expenses = income * (EXPENSE_SHARE + CHILD_EXPENSE_SHARE * Math.max(0, life.children));
    const available = money + income;
    if (expenses > available) {
      debt += expenses - available;
      money = 0;
    } else {
      money = available - expenses;
    }

    if (income > 0 && debt > BANKRUPTCY_DEBT_TO_INCOME * income) {
      debt = 0;
      stats = clampStats({ ...stats, happiness: stats.happiness + BANKRUPTCY_HAPPINESS });
      flags = { ...flags, bankruptYear: life.birthYear + life.age };
      entries.push({ age: life.age, key: 'economy.bankruptcy' });
    }
  }
  // Без работы семья содержит: ни дохода, ни расходов, долг лишь копит проценты.

  return {
    life: { ...life, money: clampMoney(money), debt: clampMoney(debt), job, stats, flags },
    entries,
  };
}
